import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import logger from "@/lib/logger";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import useBulkSelection from "@/hooks/useBulkSelection";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import TimesheetDashboard from "@/components/TimesheetDashboard";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import { formatTime, todayISOInIST } from "@/lib/dateFormat";

function UtilizationPanel({ projectsMap }) {
  // Phase 83 — replaced by TimesheetDashboard (tabbed). Keeping this stub to
  // avoid touching sibling admin surfaces that may still import it — will be
  // deleted once the full sweep is complete.
  return null;
}

function ProjectHoursSummary() {
  return null; // replaced by ProjectHours3D
}

export default function Timesheet() {
  const { user } = useAuth();
  const can = usePermissions();
  // Phase 90 — IST calendar day, not UTC (was drifting a day late-night IST).
  const [date, setDate] = useState(() => todayISOInIST());
  const [projects, setProjects] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [milestones, setMilestones] = useState({}); // project_id -> list
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState({ mode: "milestone", project_id: "", milestone_id: "", goal_id: "", hours: 1, minutes: 0, note: "" });
  const [goals, setGoals] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);

  const loadAttendance = useCallback(async () => {
    try {
      const { data } = await api.get("/attendance/today");
      setTodayAttendance(data);
    } catch (err) {
      logger.debug("Attendance check failed:", err?.message || err);
    }
  }, []);
  useEffect(() => { loadAttendance(); }, [loadAttendance]);

  const checkOut = async () => {
    try {
      await api.post("/attendance/checkout");
      toast.success("Checked out — great work today");
      loadAttendance();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 84 — allow the user to CLEAR a check-out that was set incorrectly
  // (e.g. by a bulk-imported HR sheet). Explicit user action only — no
  // auto-fire. Backend endpoint below /api/attendance/checkout/undo.
  const undoCheckOut = async () => {
    if (!window.confirm("Clear today's check-out timestamp?")) return;
    try {
      await api.post("/attendance/checkout/undo");
      toast.success("Check-out cleared — you can check out again when ready");
      loadAttendance();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/timesheet", { params: { date } });
      setEntries(data.entries || []);
      setTotal(data.total_minutes || 0);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Full project list — used only to look up names for existing entries
    // (including ones logged against a project that has since been marked
    // completed/archived, or one the viewer is no longer assigned to).
    api.get("/projects")
      .then((r) => setProjects(r.data))
      .catch((err) => logger.warn("Projects fetch failed:", err?.message || err));
    // Phase 88 — strict Project↔Timesheet assignment: the dropdown reflects
    // exactly the projects this user is allowed to log NEW time against
    // (server-filtered by Project Team membership; Founder/Admin see every
    // non-terminal project). Completed/Archived projects never appear here
    // — no client-side grace period anymore.
    api.get("/timesheet/projects")
      .then((r) => setAvailableProjects(r.data))
      .catch((err) => logger.warn("Timesheet projects fetch failed:", err?.message || err));
    api.get("/goals")
      .then((r) => setGoals(r.data))
      .catch(() => { /* goals feature may be hidden for this user */ });
  }, []);

  const loadMilestones = async (pid) => {
    if (!pid || milestones[pid]) return;
    try {
      const { data } = await api.get(`/projects/${pid}/milestones`);
      setMilestones((m) => ({ ...m, [pid]: data }));
    } catch (err) {
      logger.debug("Milestones fetch failed for", pid, err?.message || err);
    }
  };

  const add = async () => {
    if (form.mode === "milestone" && !form.project_id) { toast.error("Pick a project"); return; }
    if (form.mode === "goal" && !form.goal_id) { toast.error("Pick a goal"); return; }
    const minutes = Math.max(0, (Number(form.hours) || 0) * 60 + (Number(form.minutes) || 0));
    if (minutes <= 0) { toast.error("Enter duration"); return; }
    try {
      const body = {
        date,
        minutes,
        note: form.note,
        project_id: form.mode === "milestone" ? form.project_id : null,
        milestone_id: form.mode === "milestone" ? (form.milestone_id || null) : null,
        goal_id: form.mode === "goal" ? form.goal_id : null,
      };
      await api.post("/timesheet", body);
      setForm({ mode: form.mode, project_id: "", milestone_id: "", goal_id: "", hours: 1, minutes: 0, note: "" });
      load();
      toast.success("Logged");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/timesheet/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const enough = total >= 480;
  const hh = Math.floor(total / 60), mm = total % 60;
  const projectsMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  // Pin the internal "Analogy" project to the top of the project picker — most
  // users log in-house work (Admin & HR, Time Off, Marketing Content, …) far more
  // often than any single client project.
  // Phase 88 — availableProjects is already server-filtered (strict
  // Project Team assignment + Founder/Admin bypass + completed/archived
  // excluded with no grace period); this just keeps the internal-first sort.
  const sortedProjects = useMemo(() => {
    const internal = availableProjects.filter((p) => p.is_internal);
    const others = availableProjects.filter((p) => !p.is_internal);
    return [...internal, ...others];
  }, [availableProjects]);

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto fade-in" data-testid="timesheet-page">
      <PageHeader
        eyebrow="Daily log"
        title="Timesheet"
        subtitle="Log time against each project milestone. Minimum 8h/day required to check out."
        actions={
          <div className="flex items-center gap-3 flex-wrap justify-end">
            <BulkUploadDialog section="timesheet" label="Timesheet" onCommitted={load} />
            <div className="flex items-center gap-2">
            <Label className="label-overline">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm w-44" data-testid="timesheet-date" />
          </div>
          </div>
        }
      />

      <div className={`card-flat p-5 mb-6 border-l-4 ${enough ? "border-l-green-500" : "border-l-[#F54800]"}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="label-overline">Today&apos;s total</div>
            <div className="font-display text-3xl font-bold text-slate-900 mt-1">{hh}h {mm}m</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Tag tone={enough ? "green" : "orange"}>{enough ? "Eligible to check out" : `${480 - total} minutes short`}</Tag>
            {/* Phase 84 — Check-out button is ALWAYS visible when eligible
                (even if check_out is already set — this handles the rare
                case where a bulk-uploaded row auto-populated check_out
                incorrectly and the user needs to override the timestamp). */}
            {enough && todayAttendance?.check_in && (
              <Button onClick={checkOut} className="rounded-sm bg-green-600 hover:bg-green-700 text-white" data-testid="ts-checkout">
                <LogOut size={14} className="mr-1.5" />
                {todayAttendance?.check_out ? "Update check-out" : "Check out"}
              </Button>
            )}
            {todayAttendance?.check_out && (
              <>
                <Tag tone="slate" data-testid="ts-checkedout">Checked out at {formatTime(todayAttendance.check_out)}</Tag>
                <button
                  type="button"
                  onClick={undoCheckOut}
                  className="text-[11px] text-red-600 hover:underline"
                  data-testid="ts-undo-checkout"
                  title="Clear this check-out timestamp"
                >
                  Undo check-out
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {can("timesheet_admin") && <TimesheetDashboard initialDate={date} />}

      <div className="card-flat p-5 mb-6">
        <div className="flex items-center gap-1 mb-4">
          <button
            type="button"
            onClick={() => setForm({ ...form, mode: "milestone", goal_id: "" })}
            className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${form.mode === "milestone" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
            data-testid="ts-mode-milestone"
          >Project / milestone</button>
          {goals.length > 0 && (
            <button
              type="button"
              onClick={() => setForm({ ...form, mode: "goal", project_id: "", milestone_id: "" })}
              className={`px-3 py-1.5 text-xs rounded-sm border transition-colors ${form.mode === "goal" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
              data-testid="ts-mode-goal"
            >Personal goal</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          {form.mode === "milestone" ? (
            <>
              <div className="md:col-span-2">
                <Label className="label-overline">Project</Label>
                <Select value={form.project_id} onValueChange={(v) => { setForm({ ...form, project_id: v, milestone_id: "" }); loadMilestones(v); }}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="ts-project"><SelectValue placeholder="Pick a project" /></SelectTrigger>
                  <SelectContent>{sortedProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.is_internal ? "  — In-house" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="label-overline">Milestone</Label>
                <Select value={form.milestone_id || "none"} onValueChange={(v) => setForm({ ...form, milestone_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="ts-milestone"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— (general)</SelectItem>
                    {(milestones[form.project_id] || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <div className="md:col-span-4">
              <Label className="label-overline">Goal</Label>
              <Select value={form.goal_id} onValueChange={(v) => setForm({ ...form, goal_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="ts-goal"><SelectValue placeholder="Pick a goal" /></SelectTrigger>
                <SelectContent>
                  {goals.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.title}{g.status ? ` · ${g.status.replace("_", " ")}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="label-overline">Hours</Label>
            <Input type="number" min={0} max={24} className="rounded-sm mt-1" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} data-testid="ts-hours" />
          </div>
          <div>
            <Label className="label-overline">Min</Label>
            <Input type="number" min={0} max={59} className="rounded-sm mt-1" value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })} data-testid="ts-mins" />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-5">
            <Label className="label-overline">Note (optional)</Label>
            <Input className="rounded-sm mt-1" placeholder="What did you work on?" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="ts-note" />
          </div>
          <Button onClick={add} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="ts-add">
            <Plus size={14} className="mr-1.5" />Log time
          </Button>
        </div>
      </div>

      {/* Phase 83 — UtilizationPanel superseded by TimesheetDashboard above. */}

      <div className="card-flat">
        <div className="grid grid-cols-6 gap-4 px-5 py-3 border-b border-slate-200 label-overline">
          <div className="col-span-2">Project / Milestone</div><div>Duration</div><div className="col-span-2">Note</div><div className="text-right">Actions</div>
        </div>
        <div className="divide-row">
          {entries.map((r) => {
            const p = projectsMap[r.project_id];
            const ms = (milestones[r.project_id] || []).find((m) => m.id === r.milestone_id);
            const h = Math.floor(r.minutes / 60), m = r.minutes % 60;
            const isGoal = !!r.goal_id;
            return (
              <div key={r.id} className="grid grid-cols-6 gap-4 px-5 py-2 text-sm items-center hover-row" data-testid={`ts-row-${r.id}`}>
                <div className="col-span-2">
                  {isGoal ? (
                    <>
                      <div className="text-slate-800 inline-flex items-center gap-1.5">
                        <Tag tone="brand">goal</Tag>
                        {r.goal_title || "Personal goal"}
                      </div>
                      <div className="text-xs text-slate-500">—</div>
                    </>
                  ) : (
                    <>
                      <div className="text-slate-800">{p?.name || r.project_id}</div>
                      <div className="text-xs text-slate-500">{ms?.title || "—"}</div>
                    </>
                  )}
                </div>
                <div>{h}h {m}m</div>
                <div className="col-span-2 truncate text-slate-600">{r.note || "—"}</div>
                <div className="text-right">
                  <button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-600" data-testid={`ts-del-${r.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
          {entries.length === 0 && <div className="p-5 text-sm text-slate-500">No entries for {date}.</div>}
        </div>
      </div>

      {can("timesheet_admin") && <AdminTeamTimesheet date={date} projectsMap={projectsMap} />}
    </div>
  );
}

/** Admin-only: every team member's timesheet for the chosen day.
 *  Columns: EMPLOYEE · PROJECT / MILESTONE · DURATION · NOTE. */
function AdminTeamTimesheet({ date, projectsMap }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/timesheet/admin", { params: { date } })
      .then((r) => setRows(r.data || []))
      .catch((e) => logger.warn("admin timesheet fetch failed:", e?.message || e))
      .finally(() => setLoading(false));
  }, [date]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return rows;
    return rows.filter((r) => (r.user_name || "").toLowerCase().includes(ql));
  }, [rows, q]);

  const grandTotal = useMemo(
    () => rows.reduce((s, r) => s + (r.minutes || 0), 0),
    [rows],
  );
  const usersWithEntries = useMemo(
    () => new Set(rows.map((r) => r.user_id)).size,
    [rows],
  );

  const bulk = useBulkSelection({
    collection: "timesheets",
    ids: filtered.map((r) => r.id),
    onDone: () => window.location.reload(),
  });

  return (
    <div className="card-flat mt-8" data-testid="team-timesheet">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className="font-display font-bold text-slate-900">Team timesheet</div>
          <Tag tone="slate">{usersWithEntries} member{usersWithEntries === 1 ? "" : "s"} · {Math.floor(grandTotal / 60)}h {grandTotal % 60}m total</Tag>
        </div>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name…"
          className="rounded-sm h-8 text-xs w-56"
          data-testid="team-timesheet-search"
        />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {bulk.canBulk && (<th className="w-8 px-2 py-2"><bulk.HeaderCheckbox /></th>)}
              {["EMPLOYEE", "PROJECT / GOAL", "MILESTONE", "DURATION", "NOTE"].map((h) => (
                <th key={h} className="label-overline px-4 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => {
              const h = Math.floor((r.minutes || 0) / 60);
              const m = (r.minutes || 0) % 60;
              const isGoal = !!r.goal_id;
              return (
                <tr key={r.id} className={`hover:bg-slate-50 ${bulk.isSelected(r.id) ? "bg-orange-50/40" : ""}`} data-testid={`team-ts-${r.id}`}>
                  {bulk.canBulk && (<td className="w-8 px-2 py-2"><bulk.RowCheckbox id={r.id} /></td>)}
                  <td className="px-4 py-2 text-slate-800 whitespace-nowrap font-medium">{r.user_name || "—"}</td>
                  <td className="px-4 py-2 text-slate-700">
                    {isGoal ? <span className="inline-flex items-center gap-1.5"><Tag tone="brand">goal</Tag>{r.goal_title || "Personal goal"}</span>
                            : projectsMap[r.project_id]?.name || r.project_id || "—"}
                  </td>
                  <td className="px-4 py-2 text-slate-500 text-xs">{isGoal ? "—" : (r.milestone_id || "—")}</td>
                  <td className="px-4 py-2 font-mono text-slate-700 whitespace-nowrap">{h}h {m}m</td>
                  <td className="px-4 py-2 text-slate-600 truncate max-w-[280px]" title={r.note}>{r.note || "—"}</td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={bulk.canBulk ? 6 : 5} className="px-4 py-6 text-center text-xs text-slate-400">No timesheet entries on {date}.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
    </div>
  );
}
